/**
 * Delivery work.
 *
 * ── The mission *is* the item ───────────────────────────────────────────────
 *
 * Reward, deadline and destination live on the cargo you are carrying. There is
 * no mission log, no accept-and-abandon flow, no progress state to keep in step
 * with the world — the hold is the log. That decides a great deal for free:
 * losing the ship loses the job, hold space is the only cap on how many you can
 * run, and a heavy crate is a real liability rather than a number in a menu.
 *
 * ── Redeemed, not sold ──────────────────────────────────────────────────────
 *
 * A delivery is handed in at the mission board, not sold to the trader. Selling
 * was the first design and it conflated two things: the trader pays scrap value
 * for goods, and the board pays a *reward* for a job done. Keeping them apart
 * means a delivery item can be worth nothing to a trader — which it is — while
 * still being worth three hundred linelits to the person who asked for it.
 *
 * It also means an expired item behaves sensibly. It stops being redeemable and
 * becomes exactly what it now is: dead weight you can dump on any trader for
 * nothing.
 */
/**
 * What each band pays and how long it allows.
 *
 * ── Why distant pays what it does ───────────────────────────────────────────
 *
 * A distant job crosses a cluster boundary, and that consumes an overdrive core
 * — 150L, gone. Anything under about two hundred is therefore a job that costs
 * the player money to complete, which they will work out faster than we will.
 * The floor clears the core with something left over; the ceiling makes a long
 * haul plainly worth the trip.
 *
 * The windows are generous on purpose. A distant run is approach jumps, a
 * forty-five-second crossing and onward jumps — minutes of real time before any
 * flying — and a deadline that assumes an expert route is a deadline that
 * punishes learning the map.
 *
 * Five minutes were added to every band once the clock started running from the
 * *undock* rather than from accepting — see `startMissionClock`. That change
 * gives back the time spent in the station, and the extra five is for the part
 * it does not cover: reading the board, deciding, and getting out of the berth.
 */
export const MISSION_BANDS = {
    close: { minutes: 20, reward: { min: 18, max: 40 } },
    far: { minutes: 50, reward: { min: 55, max: 110 } },
    distant: { minutes: 95, reward: { min: 200, max: 300 } },
};
/** What a delivery item weighs. Data is nothing; a crate is a decision. */
export const MISSION_WEIGHTS = {
    // Effectively weightless, so a data run never competes with trade cargo and
    // can always be taken alongside whatever else is aboard. It is the job that
    // fills a dead leg rather than the job you build a ship for.
    data: { min: 0.2, max: 0.2 },
    // Against a starter hold of twenty, two crates is nearly full. Rolled rather
    // than fixed because a four and an eight are different decisions at the same
    // reward, and the board shows the figure before you commit.
    crate: { min: 4, max: 8 },
};
export const MISSION_LABELS = {
    data: 'data drive',
    crate: 'delivery crate',
};
/** What the board pays for these. Nothing — the reward is not a sale price. */
export const MISSION_ITEM_VALUE = 0;
/**
 * Roll one offer.
 *
 * The destination is supplied because finding one means walking the galaxy, and
 * that belongs above this module — the same split `tutorialStart` uses.
 */
export function rollMission(random, options) {
    const rules = MISSION_BANDS[options.band];
    const weight = MISSION_WEIGHTS[options.kind];
    const between = (min, max) => min + random.float() * (max - min);
    return {
        id: options.id,
        kind: options.kind,
        band: options.band,
        reward: Math.round(between(rules.reward.min, rules.reward.max)),
        // A tenth of a unit is as fine as a hold reads; more would be noise.
        weight: Math.round(between(weight.min, weight.max) * 10) / 10,
        destination: options.destination,
        minutes: rules.minutes,
    };
}
/**
 * Take the job.
 *
 * **The clock does not start here.** The crate goes in the hold with its
 * allowance recorded and no expiry, and `startMissionClock` sets one when the
 * ship leaves the pad — see `expiresAt` for why.
 *
 * `now` is no longer read and is kept in the signature because callers pass it
 * and because a deadline that *did* start here would want it. Removing it would
 * make re-adding one a change at every call site.
 */
export function acceptMission(brief, _now = Date.now()) {
    return {
        id: brief.id,
        kind: brief.kind,
        band: brief.band,
        reward: brief.reward,
        destination: brief.destination,
        minutes: brief.minutes,
        expiresAt: null,
    };
}
/**
 * Start the clock on a job that has not begun one.
 *
 * Called on the undock, for everything in the hold at once. Idempotent by
 * construction: a job already counting down keeps the deadline it has, so a
 * second undock in the same trip — or a client that reports one twice — cannot
 * extend it.
 *
 * A job with no allowance never gets an expiry, which is what makes the
 * introduction's delivery survive the trip however long it takes.
 */
export function startMissionClock(mission, now = Date.now()) {
    if (mission.expiresAt !== null)
        return mission;
    if (mission.minutes === null)
        return mission;
    return { ...mission, expiresAt: now + mission.minutes * 60_000 };
}
/** Whether the deadline has passed. A job without one never expires. */
export function missionExpired(mission, now = Date.now()) {
    return mission.expiresAt !== null && now >= mission.expiresAt;
}
/**
 * How long is left, in whole seconds. Never negative.
 *
 * Null for a job with no deadline — which is a different answer from zero, and
 * the reason this returns it rather than something large: `Infinity` formats as
 * nonsense and `Number.MAX_SAFE_INTEGER` reads as "eight hundred thousand
 * hours", both of which are worse than saying there is no clock.
 */
export function missionRemaining(mission, now = Date.now()) {
    if (mission.expiresAt === null)
        return null;
    return Math.max(0, Math.round((mission.expiresAt - now) / 1000));
}
/** Whether this job is waiting for the ship to leave before it starts counting. */
export function missionPending(mission) {
    return mission.expiresAt === null && mission.minutes !== null;
}
/**
 * Whether this station can pay out this job right now.
 *
 * Both halves matter and they fail differently: the wrong station is a mistake
 * you can still fix by flying there, and an expired one is not.
 */
export function missionRedeemable(mission, stationId, now = Date.now()) {
    return mission.destination.stationId === stationId && !missionExpired(mission, now);
}
/** A deadline, in the shortest form that still reads as urgent. */
export function formatRemaining(seconds) {
    if (seconds === null)
        return 'no deadline';
    if (seconds <= 0)
        return 'expired';
    if (seconds < 60)
        return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60)
        return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}
/**
 * An accepted job, as the thing you carry.
 *
 * The mission *is* the cargo — there is no separate record of it anywhere, and
 * that is deliberate: delivering is a station finding something in your hold it
 * can pay out for, and a deadline passing leaves you holding dead weight rather
 * than needing a status somewhere to be moved to "failed".
 *
 * Lifted out of the client when the server needed to hand out the same item, so
 * that an accepted job means one thing on both sides.
 */
export function deliveryItem(mission, weight) {
    return {
        // Unique per job, so two crates to different places never stack into one
        // row and lose a destination between them.
        id: `mission-${mission.id}`,
        kind: 'resource',
        label: MISSION_LABELS[mission.kind],
        count: 1,
        weight,
        // Worth nothing to a trader. The reward is paid at the destination.
        value: 0,
        mission,
    };
}
// ── The introduction's delivery ──────────────────────────────────────────────
/**
 * The introduction's delivery.
 *
 * ── Why the first sixty linelits are earned rather than given ───────────────
 *
 * They used to be in the account at creation, which quietly taught the wrong
 * thing twice over: that money appears, and that a mission board is somewhere
 * you go once you already know what you are doing. A brand new pilot with sixty
 * linelits and no idea what they are for is being handed the end of a loop they
 * have not seen the start of.
 *
 * So the first thing a new pilot does after landing is take a job. The board at
 * the station the introduction talks them into has exactly one offer on it —
 * this — and running it is a complete pass through the game's actual income:
 * read the board, take the crate, fly it somewhere, hand it over, get paid.
 *
 * ── What makes it special ───────────────────────────────────────────────────
 *
 * **No deadline.** `minutes` is null, so `startMissionClock` never gives it an
 * expiry however long the trip takes. A pilot still learning the throttle
 * should not be able to fail the first thing they are asked to do by reading
 * slowly.
 *
 * **It is the only thing on the board.** Not one of four — see `tutorialBoard`.
 * A first mission board with four jobs on it is a decision, and this is meant
 * to be an instruction.
 *
 * **It goes somewhere else.** The whole point is the trip: docking, taking a
 * job, leaving again and arriving somewhere new is the loop, and a delivery to
 * the counter you are standing at would teach none of it.
 *
 * **Light.** Two units against a starter hold of twenty, so it is never the
 * reason something else will not fit.
 */
export const TUTORIAL_MISSION_ID = 'tutorial-delivery';
/** What handing it in pays. The opening balance, earned. */
export const TUTORIAL_MISSION_REWARD = 60;
export const TUTORIAL_MISSION_WEIGHT = 2;
/** Whether this is the introduction's crate, wherever it turns up. */
export function isTutorialMission(mission) {
    return mission.id === TUTORIAL_MISSION_ID;
}
/** The same question, of an offer rather than of something in the hold. */
export function isTutorialOffer(brief) {
    return brief.id === TUTORIAL_MISSION_ID;
}
/**
 * The introduction's job, as an offer on a board.
 *
 * The destination is supplied because choosing one means walking the galaxy,
 * which belongs above this module — the same split every other mission uses.
 * See `tutorialBoard`, which is what picks it.
 */
export function tutorialOffer(destination) {
    return {
        id: TUTORIAL_MISSION_ID,
        kind: 'crate',
        // `close` because it is meant to be a short first trip, and because the band
        // is what decides how far `pickDestination` is allowed to look.
        band: 'close',
        reward: TUTORIAL_MISSION_REWARD,
        weight: TUTORIAL_MISSION_WEIGHT,
        destination,
        minutes: null,
    };
}
//# sourceMappingURL=missions.js.map